import { useState, useEffect, useContext } from "react";
import api from "../services/api-service";
import { AuthContext } from "../contexts/AuthContext";
import "../styles/past-incidents.css";
import { formatDateTimeCET } from "../util/DateFormatter";

export default function PastIncidents() {
  const { user, loading } = useContext(AuthContext);

  const [incidents, setIncidents] = useState([]);
  const [error, setError] = useState(null);
  const [incidentsLoading, setIncidentsLoading] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      const fetchIncidents = async () => {
        try {
          setIncidentsLoading(true);
          setError(null);

          const res = await api.get(`/incidents/user/${user.id}`);
          setIncidents(res.data || []);
        } catch (err) {
          console.error("Failed to fetch incidents", err);
          setError(
            "Failed to load incidents. Check your internet connection and try again.",
          );
        } finally {
          setIncidentsLoading(false);
        }
      };

      fetchIncidents();
    }
  }, [user, loading]);

  if (loading) {
    return <div className="page-state loading">Loading incidents...</div>;
  }

  if (!user) {
    return (
      <div className="page-state">Please log in to view your incidents.</div>
    );
  }

  if (incidentsLoading) {
    return <div className="page-state loading">Loading incidents...</div>;
  }

  return (
    <div className="page-container">
      <h1>Past Incidents</h1>

      {error && <div className="page-state error">{error}</div>}

      {!error && incidents.length === 0 ? (
        <div className="page-state">You have no past incidents.</div>
      ) : (
        <ul className="incident-list">
          {incidents.map((incident) => {
            const status = incident.status?.toLowerCase() || "unknown";

            const statusClass =
              status === "approved"
                ? "incident-approved"
                : status === "pending"
                  ? "incident-pending"
                  : "incident-rejected";

            const addressParts = incident.location?.address
              ? incident.location.address.split(",")
              : [];

            const shortLocation =
              addressParts.length >= 2
                ? `${addressParts[0].trim()}, ${addressParts[1].trim()}`
                : incident.location?.address ||
                  incident.location?.neighbourhood ||
                  "Unknown location";

            const formattedDate = formatDateTimeCET(incident.createdAt);

            return (
              <li key={incident.id} className={`incident-item ${statusClass}`}>
                <div className="incident-top">
                  <h3 className="incident-title">
                    {incident.typeName || "Unknown type"}
                  </h3>
                  <span className={`status-badge ${status}`}>
                    {incident.status || "UNKNOWN"}
                  </span>
                </div>

                <div className="incident-details">
                  <p>
                    <strong>Subtype:</strong> {incident.subtypeName || "None"}
                  </p>
                  <p>
                    <strong>Description:</strong>{" "}
                    {incident.descriptionEn ||
                      incident.descriptionSr ||
                      "No description"}
                  </p>
                  <p>
                    <strong>Reported at:</strong> {formattedDate}
                  </p>
                  <p>
                    <strong>Location:</strong> {shortLocation}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
